import React, { useRef } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { toast } from 'react-toastify';
import { bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';
import {
  deriveContractRevenueAndGPForDisplay,
  mapBackendToFrontendFields,
  parseDateString,
  formatDateForChartAPI,
  preserveGlobalFields,
} from '../../bidAnalysisTemplate.logic';
import classes from '../../bidAnalysisTemplate.module.scss';
import { NCFormContent } from './NCFormContent';
import {
  CogsBuildupSection,
  CommercialPriceRankingSection,
  CompetitiveAnalysisSection,
  ContractPriceRankingSection,
  CurrentMarketStateSection,
  MarketLandscapeSection,
  PackSizeOutputSection,
  PricingCalculatorSection,
  WACAnalysisSection,
} from '../sections';
import { NCGlobalOutputSection } from '../sections/NCGlobalOutputSection';
import { FormField } from '../shared/FormField';
import { SubSectionHeader } from '../shared/SubSectionHeader';
import { SECTION_NAMES } from '../../constants/sectionNames';
import { formatNDC } from '../../shared/utils/formatNDC';

export interface NCTemplateContentCtx {
  sectionRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;
  templateData: any;
  formData: any;
  yearFormData: Record<number, Record<number, any>>;
  activePackSizeTab: number | null;
  activeYearTab: number | null;
  setActivePackSizeTab: (tab: number) => void;
  setActiveYearTab: (tab: number) => void;
  analysisType: string;
  gsmsHndlgType: string;
  supplierName: string;
  supplierOptions: any[];
  contractsList: any[];
  handlingTypesList: any[];
  pricingValidatedChecked: boolean;
  validationErrors: Record<string, string>;
  handleFieldChange: (field: string, value: any) => void;
  handleFieldBlur: (field: string, event?: any) => void;
  handleFieldKeyDown: (field: string, e: React.KeyboardEvent) => void;
  setAnalysisType: (type: string) => void;
  handleGsmsHndlgTypeChange: (type: string) => void;
  setSupplierName: (name: string) => void;
  isSaving: boolean;
  setYearFormData: (updater: any) => void;
  setFormData: (updater: any) => void;
  setPackSizeFormData: (updater: any) => void;
  notes: any[];
  handleNotesClick: (section: string, event?: any) => void;
  selectedConcept: string | null;
  setSelectedConcept: (concept: string | null) => void;
  concepts: { label: any; value: any }[];
  packSizeTabs: any[];
  handlePackSizeTabChange: (tab: number) => void;
  handleYearTabChange: (tab: number) => void;
  SECTION_NAMES: Record<string, string>;
  SECTION_DISPLAY_NAMES: Record<string, string>;
  bidDetails: any;
  templateType: string;
  productType: string | null;
  bidId: string | undefined;
  allPackSizeTabs: Array<{ index: number; packSize: string; conceptName: string }>;
  yearTabs: Array<{ index: number; label: string; year: number | null }>;
  packSizeFormData: Record<number, any>;
  yearFormDataRef: React.MutableRefObject<Record<number, Record<number, any>>>;
  ndcOptions: any[];
  ndcValidated: boolean;
  setNdcValidated: (v: boolean) => void;
  isLoadingNdcData: boolean;
  setIsLoadingNdcData: (v: boolean) => void;
  isValidatingNdc: boolean;
  setIsValidatingNdc: (v: boolean) => void;
  copyForAllYears: () => void;
  calculate5YearSummary: () => any;
  marketLandscapeData: any[];
  setMarketLandscapeData: (data: any[]) => void;
  chartDateRange: { startDate: Date | null; endDate: Date | null };
  tempDateRange: { startDate: Date | null; endDate: Date | null };
  calendarViewDates: { startViewDate: Date | null; endViewDate: Date | null };
  selectedCompany: string | null;
  isLoadingMarketTrends: boolean;
  isLoadingMarketShare: boolean;
  volumeByCot: Array<{ cot: string; volume: number }>;
  isLoadingVolumeByCot: boolean;
  loadVolumeByCot: (
    labeller?: string | null,
    customStartDate?: Date | string | null,
    customEndDate?: Date | string | null
  ) => Promise<void>;
  marketTrendsOptions: any;
  handleDateRangeChange: (start: Date | null, end: Date | null) => void;
  handleApplyDateFilter: () => void;
  setCalendarViewDates: (updater: any) => void;
  handleMarketShareClick: (vendor: string) => void;
  setShowChartModal: (v: boolean) => void;
  contractPriceRanking: any[];
  expandedRows: any;
  handleExpandedRowsChange: (e: any) => void;
  isLoadingContractPriceRanking: boolean;
  loadNotes: () => void;
  loadContractPriceRanking: (
    startDate?: string,
    endDate?: string,
    forceRefresh?: boolean,
    limit?: number
  ) => void;
  loadMarketSalesTrendData: (company: string | null, forceRefresh?: boolean) => Promise<void>;
  loadMarketShareByVolume: (forceRefresh?: boolean) => Promise<void>;
  formatTargetLaunchDateForDisplay: (date: any, yearOffset?: number) => string;
  getCurrentUser: () => string;
  bidOwner: string | null;
  productName: string;
  setChartDateRange: (range: { startDate: Date | null; endDate: Date | null }) => void;
  handleTempDateRangeChange: (start: Date | null, end: Date | null) => void;
  ndcNotFoundShownRef: React.MutableRefObject<boolean>;
  hasLoadedInitialData: React.MutableRefObject<boolean>;
  recalculateFields: (data: any, changedField?: string) => any;
  /** SCSS module classes for NCFormContent and section styling */
  classes?: Record<string, string>;
}

export function NCTemplateContent({ ctx }: { ctx: NCTemplateContentCtx }) {
  const {
    sectionRefs,
    expandedSections,
    toggleSection,
    templateData,
    formData,
    yearFormData,
    activePackSizeTab,
    activeYearTab,
    setActivePackSizeTab: _setActivePackSizeTab,
    setActiveYearTab: _setActiveYearTab,
    analysisType,
    gsmsHndlgType,
    supplierName,
    supplierOptions: _supplierOptions,
    contractsList: _contractsList,
    handlingTypesList: _handlingTypesList,
    pricingValidatedChecked: _pricingValidatedChecked,
    validationErrors,
    handleFieldChange,
    handleFieldBlur,
    handleFieldKeyDown,
    setAnalysisType: _setAnalysisType,
    handleGsmsHndlgTypeChange: _handleGsmsHndlgTypeChange,
    setSupplierName: _setSupplierName,
    isSaving: _isSaving,
    setYearFormData,
    setFormData,
    setPackSizeFormData,
    notes,
    handleNotesClick,
    selectedConcept,
    setSelectedConcept,
    concepts: _concepts,
    packSizeTabs,
    handlePackSizeTabChange,
    handleYearTabChange,
    SECTION_NAMES: SECTION_NAMES_CTX,
    bidDetails,
    templateType,
    productType,
    bidId,
    allPackSizeTabs,
    yearTabs,
    packSizeFormData,
    yearFormDataRef,
    ndcOptions,
    ndcValidated: _ndcValidated,
    setNdcValidated,
    isLoadingNdcData,
    setIsLoadingNdcData,
    isValidatingNdc,
    setIsValidatingNdc,
    copyForAllYears,
    calculate5YearSummary,
    marketLandscapeData,
    setMarketLandscapeData,
    chartDateRange,
    tempDateRange,
    calendarViewDates,
    selectedCompany,
    isLoadingMarketTrends,
    isLoadingMarketShare,
    volumeByCot,
    isLoadingVolumeByCot,
    marketTrendsOptions,
    handleDateRangeChange,
    handleApplyDateFilter,
    setCalendarViewDates,
    handleMarketShareClick,
    setShowChartModal,
    contractPriceRanking,
    expandedRows,
    handleExpandedRowsChange,
    isLoadingContractPriceRanking,
    loadNotes,
    loadContractPriceRanking,
    loadMarketSalesTrendData,
    loadMarketShareByVolume,
    formatTargetLaunchDateForDisplay,
    getCurrentUser,
    bidOwner,
    productName: _productName,
    setChartDateRange,
    handleTempDateRangeChange: _handleTempDateRangeChange,
    ndcNotFoundShownRef: _ndcNotFoundShownRef,
    hasLoadedInitialData,
    recalculateFields,
  } = ctx;

  const SECTION_NAMES_LOCAL = SECTION_NAMES_CTX || SECTION_NAMES;
  const ndcDropdownRef = useRef<any>(null);

  return (
    <>
      <NCFormContent ctx={ctx} />
      {/* Product Details Section with Year Tabs - NC: header/concept/pack size in NCFormContent; Full keeps year tabs + rest */}
      <div ref={el => (sectionRefs.current['product-info'] = el)} className={classes.section}>
        {templateType !== 'nc' && (
          <div className={classes.sectionHeader} onClick={() => toggleSection('productDetails')}>
            <h2>Product Details</h2>
            <Button
              icon="pi pi-comment"
              className={classes.notesButton}
              onClick={e => {
                e.stopPropagation();
                handleNotesClick(SECTION_NAMES_LOCAL.PRODUCT_DETAILS, e);
              }}
              label={`Notes (${notes.filter(n => n.section_name === SECTION_NAMES_LOCAL.PRODUCT_DETAILS).length})`}
              text
              size="small"
            />
            <i
              className={`pi ${expandedSections.productDetails ? 'pi-chevron-up' : 'pi-chevron-down'}`}
            />
          </div>
        )}
        {expandedSections.productDetails && (
          <div className={classes.sectionContent}>
            {/* NC: concept/pack size in NCFormContent; below is year tabs + rest */}
            {/* Year Tabs - Only show when pack size is selected */}
            {selectedConcept && activePackSizeTab !== null && yearTabs.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '0.5rem',
                  }}
                >
                  <label
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#374151',
                      display: 'block',
                    }}
                  >
                    Years
                  </label>
                  {activeYearTab === 0 && (
                    <Button
                      label="Copy for all years"
                      icon="pi pi-copy"
                      size="small"
                      onClick={copyForAllYears}
                      style={{ marginLeft: 'auto' }}
                    />
                  )}
                </div>
                <div className={classes.ndcTabs}>
                  {yearTabs.map(tab => (
                    <div
                      key={tab.index}
                      className={`${classes.ndcTab} ${activeYearTab === tab.index ? classes.active : ''}`}
                      onClick={() => handleYearTabChange(tab.index)}
                    >
                      {tab.label} {tab.year ? `(${tab.year})` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Supplier Bulk NDC Selector - Only show when pack size and year are selected (including Base Year and all Option Years) */}
            {selectedConcept &&
              activePackSizeTab !== null &&
              activeYearTab !== null &&
              activeYearTab !== 5 &&
              activeYearTab >= 0 &&
              activeYearTab <= 4 && (
                <>
                  <div className={classes.mfgNdcSection} style={{ marginBottom: '1.5rem' }}>
                    <div className={classes.formField} style={{ maxWidth: '500px' }}>
                      <label>
                        Supplier Bulk NDC <span className={classes.optional}>(Optional)</span>
                      </label>
                      <div className={classes.ndcInputGroup}>
                        <Dropdown
                          ref={ndcDropdownRef}
                          value={(() => {
                            // For NC templates, check yearFormData; for MAS, check formData
                            const currentTemplateType = bidDetails?.template_type || 'mas';
                            const isNC = currentTemplateType === 'nc';
                            const currentData =
                              isNC &&
                              activePackSizeTab !== null &&
                              activeYearTab !== null &&
                              activeYearTab !== 5
                                ? yearFormData[activePackSizeTab]?.[activeYearTab] || formData
                                : formData;
                            return (
                              currentData.selectedMfgNdc ||
                              currentData.mfg_ndc ||
                              currentData.gsms_mfg_ndc ||
                              ''
                            );
                          })()}
                          options={ndcOptions.map(ndc => ({
                            label: ndc.ndc11,
                            value: ndc.ndc11,
                          }))}
                          onChange={async e => {
                            ndcDropdownRef.current?.hide();
                            const selectedNdc = e.value;
                            handleFieldChange('selectedMfgNdc', selectedNdc);
                            // Reset validation state when NDC changes
                            setNdcValidated(false);
                            setIsLoadingNdcData(false);

                            // When NDC is selected, store it
                            if (selectedNdc) {
                              const lockedData = {
                                ...formData,
                                selectedMfgNdc: selectedNdc,
                                mfg_ndc: selectedNdc,
                                gsms_mfg_ndc: null, // Not validated yet - will be set on Validate
                                ndcValidated: false,
                              };
                              setFormData(lockedData);

                              // For NC template, update yearFormData for ALL years (0-4)
                              // Supplier Bulk NDC is the same for all years
                              if (activePackSizeTab !== null) {
                                setYearFormData(prev => {
                                  const updated = { ...prev };
                                  if (!updated[activePackSizeTab]) {
                                    updated[activePackSizeTab] = {};
                                  }
                                  // Update all years (0-4) with the same NDC values
                                  for (let yearIndex = 0; yearIndex <= 4; yearIndex++) {
                                    updated[activePackSizeTab][yearIndex] = {
                                      ...updated[activePackSizeTab][yearIndex],
                                      ...lockedData,
                                    };
                                  }
                                  return updated;
                                });

                                // Also update packSizeFormData so discount fetch useEffect triggers
                                setPackSizeFormData(prev => ({
                                  ...prev,
                                  [activePackSizeTab]: {
                                    ...prev[activePackSizeTab],
                                    ...lockedData,
                                  },
                                }));
                              }
                            }
                          }}
                          placeholder={
                            ndcOptions.length === 0
                              ? 'No available NDC found'
                              : 'Select Supplier Bulk NDC'
                          }
                          style={{ flex: 1 }}
                          filter
                          disabled={ndcOptions.length === 0}
                        />
                        {(() => {
                          const isDisabled = isValidatingNdc || isLoadingNdcData;
                          return (
                            <Button
                              type="button"
                              label="Fetch Current Market State"
                              icon={isValidatingNdc ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
                              className={classes.validateBtn}
                              loading={isValidatingNdc}
                              onClick={async () => {
                                const currentData =
                                  activePackSizeTab !== null &&
                                  activeYearTab !== null &&
                                  activeYearTab !== 5
                                    ? yearFormData[activePackSizeTab]?.[activeYearTab] || formData
                                    : formData;
                                const ndcToUse =
                                  [
                                    currentData.selectedMfgNdc,
                                    currentData.mfg_ndc,
                                    currentData.gsms_mfg_ndc,
                                  ].find(v => v != null && String(v).trim() !== '') ?? null;
                                if (
                                  !supplierName ||
                                  !currentData.conceptName ||
                                  !currentData.packSize
                                ) {
                                  console.warn('Missing required fields for fetch');
                                  return;
                                }
                                setIsValidatingNdc(true);
                                try {
                                  const productDetailResponse =
                                    await bidAnalysisServices.getProductDetail(
                                      parseInt(bidId || '0'),
                                      {
                                        concept_name: currentData.conceptName,
                                        pack_size: parseFloat(currentData.packSize),
                                        manufacturer: supplierName,
                                        ...(ndcToUse ? { mfg_ndc: ndcToUse } : {}),
                                      }
                                    );

                                  const productDetail =
                                    productDetailResponse?.data || productDetailResponse;

                                  if (productDetail) {
                                    const mappedCogsData =
                                      mapBackendToFrontendFields(productDetail);

                                    const updatedData = {
                                      ...currentData,
                                      ...(ndcToUse
                                        ? {
                                            selectedMfgNdc: ndcToUse,
                                            mfg_ndc: ndcToUse,
                                            gsms_mfg_ndc: ndcToUse,
                                            gsms_ndc: currentData.gsms_ndc || formatNDC(ndcToUse),
                                            ndcValidated: true,
                                          }
                                        : {}),
                                      vaAnnualNcEst:
                                        productDetail.va_annual_nc_est?.toString() || '0',
                                      dodAnnualNcEst:
                                        productDetail.dod_annual_nc_est?.toString() || '0',
                                      ihsAnnualNcEst:
                                        productDetail.ihs_annual_nc_est?.toString() || '0',
                                      bopAnnualNcEst:
                                        productDetail.bop_annual_nc_est?.toString() || '0',
                                      ncEstAnnualUsage:
                                        productDetail.nc_est_annual_usage?.toString() || '0',
                                      usagePeriodStartDate:
                                        productDetail.start_date ||
                                        currentData.usagePeriodStartDate,
                                      usagePeriodEndDate:
                                        productDetail.end_date || currentData.usagePeriodEndDate,
                                      // NC: map supplier_wac to Supplier Comparable WAC; leave wac unchanged
                                      wac: currentData.wac ?? '',
                                      supplier_comparable_wac:
                                        productDetail.supplier_wac != null
                                          ? productDetail.supplier_wac.toString()
                                          : (currentData.supplier_comparable_wac ?? ''),
                                      supplier_wac: productDetail.supplier_wac ?? null,
                                      // Preserve COGS values from API (COGS Sync) - only if they exist in API response
                                      // These values should be preserved and not recalculated unless dependencies change
                                      ...(mappedCogsData.gsmsBottleCost !== null &&
                                      mappedCogsData.gsmsBottleCost !== undefined
                                        ? { gsmsBottleCost: mappedCogsData.gsmsBottleCost }
                                        : {}),
                                      ...(mappedCogsData.gsmsCapCost !== null &&
                                      mappedCogsData.gsmsCapCost !== undefined
                                        ? { gsmsCapCost: mappedCogsData.gsmsCapCost }
                                        : {}),
                                      ...(mappedCogsData.gsmsLabelCost !== null &&
                                      mappedCogsData.gsmsLabelCost !== undefined
                                        ? { gsmsLabelCost: mappedCogsData.gsmsLabelCost }
                                        : {}),
                                      ...(mappedCogsData.gsmsInsertCost !== null &&
                                      mappedCogsData.gsmsInsertCost !== undefined
                                        ? { gsmsInsertCost: mappedCogsData.gsmsInsertCost }
                                        : {}),
                                      ...(mappedCogsData.gsmsBoxCartonCost !== null &&
                                      mappedCogsData.gsmsBoxCartonCost !== undefined
                                        ? {
                                            gsmsBoxCartonCost: mappedCogsData.gsmsBoxCartonCost,
                                          }
                                        : {}),
                                      ...(mappedCogsData.gsmsDesiccantCost !== null &&
                                      mappedCogsData.gsmsDesiccantCost !== undefined
                                        ? {
                                            gsmsDesiccantCost: mappedCogsData.gsmsDesiccantCost,
                                          }
                                        : {}),
                                      ...(mappedCogsData.outsourceRepackFee !== null &&
                                      mappedCogsData.outsourceRepackFee !== undefined
                                        ? {
                                            outsourceRepackFee: mappedCogsData.outsourceRepackFee,
                                          }
                                        : {}),
                                      ...(mappedCogsData.blendedWhlsFee !== null &&
                                      mappedCogsData.blendedWhlsFee !== undefined
                                        ? { blendedWhlsFee: mappedCogsData.blendedWhlsFee }
                                        : {}),
                                      ...(mappedCogsData.gsmsFrtCost !== null &&
                                      mappedCogsData.gsmsFrtCost !== undefined
                                        ? { gsmsFrtCost: mappedCogsData.gsmsFrtCost }
                                        : {}),
                                      ...(mappedCogsData.pv2Percent !== null &&
                                      mappedCogsData.pv2Percent !== undefined
                                        ? { pv2Percent: mappedCogsData.pv2Percent }
                                        : {}),
                                      ...(mappedCogsData.gsmsLabor !== null &&
                                      mappedCogsData.gsmsLabor !== undefined
                                        ? { gsmsLabor: mappedCogsData.gsmsLabor }
                                        : {}),
                                      // Also preserve gsmsCostFromMfg if it comes from API
                                      ...(mappedCogsData.gsmsCostFromMfg !== null &&
                                      mappedCogsData.gsmsCostFromMfg !== undefined
                                        ? {
                                            gsmsCostFromMfg: mappedCogsData.gsmsCostFromMfg,
                                          }
                                        : {}),
                                      ...(mappedCogsData.gsmsNetCostFromMfg !== null &&
                                      mappedCogsData.gsmsNetCostFromMfg !== undefined
                                        ? {
                                            gsmsNetCostFromMfg: mappedCogsData.gsmsNetCostFromMfg,
                                          }
                                        : {}),
                                    };
                                    setFormData(preserveGlobalFields(updatedData, formData));

                                    // For NC template, update yearFormData for ALL years (0-4)
                                    // Supplier Bulk NDC and GSMS NDC are the same for all years
                                    if (activePackSizeTab !== null) {
                                      setYearFormData(prev => {
                                        const updated = { ...prev };
                                        if (!updated[activePackSizeTab]) {
                                          updated[activePackSizeTab] = {};
                                        }
                                        // Update all years (0-4) with the same NDC values
                                        // But preserve year-specific fields (usage fields, dates, etc.)
                                        // COGS values from API should be preserved for all years
                                        // NOTE: blendedWhlsFee should NOT be copied - it must be calculated per year based on year-specific dates
                                        for (let yearIndex = 0; yearIndex <= 4; yearIndex++) {
                                          const currentYearData =
                                            updated[activePackSizeTab][yearIndex] || {};
                                          updated[activePackSizeTab][yearIndex] = {
                                            ...currentYearData,
                                            // Update NDC fields for all years (only when we had NDC in payload)
                                            ...(updatedData.selectedMfgNdc != null
                                              ? {
                                                  selectedMfgNdc: updatedData.selectedMfgNdc,
                                                  mfg_ndc: updatedData.mfg_ndc,
                                                  gsms_mfg_ndc: updatedData.gsms_mfg_ndc,
                                                  gsms_ndc: updatedData.gsms_ndc,
                                                  ndcValidated: updatedData.ndcValidated,
                                                }
                                              : {}),
                                            // Preserve COGS values from API for all years (COGS Sync)
                                            ...(updatedData.gsmsBottleCost !== undefined
                                              ? {
                                                  gsmsBottleCost: updatedData.gsmsBottleCost,
                                                }
                                              : {}),
                                            ...(updatedData.gsmsCapCost !== undefined
                                              ? { gsmsCapCost: updatedData.gsmsCapCost }
                                              : {}),
                                            ...(updatedData.gsmsLabelCost !== undefined
                                              ? { gsmsLabelCost: updatedData.gsmsLabelCost }
                                              : {}),
                                            ...(updatedData.gsmsInsertCost !== undefined
                                              ? {
                                                  gsmsInsertCost: updatedData.gsmsInsertCost,
                                                }
                                              : {}),
                                            ...(updatedData.gsmsBoxCartonCost !== undefined
                                              ? {
                                                  gsmsBoxCartonCost: updatedData.gsmsBoxCartonCost,
                                                }
                                              : {}),
                                            ...(updatedData.gsmsDesiccantCost !== undefined
                                              ? {
                                                  gsmsDesiccantCost: updatedData.gsmsDesiccantCost,
                                                }
                                              : {}),
                                            ...(updatedData.outsourceRepackFee !== undefined
                                              ? {
                                                  outsourceRepackFee:
                                                    updatedData.outsourceRepackFee,
                                                }
                                              : {}),
                                            // DO NOT copy blendedWhlsFee - it must be calculated per year with year-specific dates
                                            ...(updatedData.gsmsFrtCost !== undefined
                                              ? { gsmsFrtCost: updatedData.gsmsFrtCost }
                                              : {}),
                                            ...(updatedData.pv2Percent !== undefined
                                              ? { pv2Percent: updatedData.pv2Percent }
                                              : {}),
                                            ...(updatedData.gsmsLabor !== undefined
                                              ? { gsmsLabor: updatedData.gsmsLabor }
                                              : {}),
                                            ...(updatedData.gsmsCostFromMfg !== undefined
                                              ? {
                                                  gsmsCostFromMfg: updatedData.gsmsCostFromMfg,
                                                }
                                              : {}),
                                            ...(updatedData.gsmsNetCostFromMfg !== undefined
                                              ? {
                                                  gsmsNetCostFromMfg:
                                                    updatedData.gsmsNetCostFromMfg,
                                                }
                                              : {}),
                                            // Only update usage fields for the current year
                                            ...(yearIndex === activeYearTab
                                              ? {
                                                  vaAnnualNcEst: updatedData.vaAnnualNcEst,
                                                  dodAnnualNcEst: updatedData.dodAnnualNcEst,
                                                  ihsAnnualNcEst: updatedData.ihsAnnualNcEst,
                                                  bopAnnualNcEst: updatedData.bopAnnualNcEst,
                                                  ncEstAnnualUsage: updatedData.ncEstAnnualUsage,
                                                  usagePeriodStartDate:
                                                    updatedData.usagePeriodStartDate,
                                                  usagePeriodEndDate:
                                                    updatedData.usagePeriodEndDate,
                                                  wac: updatedData.wac,
                                                  supplier_comparable_wac:
                                                    updatedData.supplier_comparable_wac,
                                                  supplier_wac: updatedData.supplier_wac,
                                                }
                                              : {}),
                                          };
                                        }

                                        // After syncing COGS data, recalculate blendedWhlsFee for each year with year-specific dates
                                        // This ensures each year gets the correct blendedWhlsFee based on its calculated date
                                        // Use recalculateFieldsLogic directly since we're inside a state update
                                        for (let yearIndex = 0; yearIndex <= 4; yearIndex++) {
                                          const yearData = updated[activePackSizeTab][yearIndex];
                                          if (yearData) {
                                            // Trigger recalculation for this year to calculate blendedWhlsFee with year-specific date
                                            const recalculatedYearData = recalculateFields({
                                              updatedData: yearData,
                                              changedField: 'targetLaunchDate', // Trigger blendedWhlsFee recalculation
                                              productType: 'indirect', // NC uses indirect formulas
                                              templateData,
                                              analysisType,
                                              contractPriceRanking: contractPriceRanking as Array<{
                                                price_rank: number;
                                                price: number;
                                              }>,
                                              templateType: 'nc',
                                              supplierName: supplierName,
                                              yearIndex: yearIndex, // Pass yearIndex for year-specific date calculation
                                            });
                                            updated[activePackSizeTab][yearIndex] =
                                              recalculatedYearData;
                                          }
                                        }

                                        return updated;
                                      });
                                    }

                                    setNdcValidated(true);

                                    // Set chart date range from usage period if available
                                    const usageStart =
                                      productDetail.start_date || formData.usagePeriodStartDate;
                                    const usageEnd =
                                      productDetail.end_date || formData.usagePeriodEndDate;
                                    if (usageStart && usageEnd) {
                                      try {
                                        const parsedStart = parseDateString(usageStart);
                                        const parsedEnd = parseDateString(usageEnd);

                                        if (
                                          parsedStart &&
                                          parsedEnd &&
                                          !isNaN(parsedStart.getTime()) &&
                                          !isNaN(parsedEnd.getTime())
                                        ) {
                                          setChartDateRange({
                                            startDate: parsedStart,
                                            endDate: parsedEnd,
                                          });
                                          setCalendarViewDates({
                                            startViewDate: parsedStart,
                                            endViewDate: parsedEnd,
                                          });
                                        }
                                      } catch (error) {
                                        console.error(
                                          'Error setting chart date range from usage period:',
                                          error
                                        );
                                      }
                                    }

                                    toast.info(
                                      'Current market state data fetched. Current data overwritten. Please click Save to save the bid.',
                                      { autoClose: 5000 }
                                    );

                                    // Load market data after validation
                                    if (updatedData.conceptName && updatedData.packSize) {
                                      const startDate =
                                        productDetail.start_date ||
                                        updatedData.usagePeriodStartDate;
                                      const endDate =
                                        productDetail.end_date || updatedData.usagePeriodEndDate;

                                      await loadMarketSalesTrendData(null, true);
                                      await loadMarketShareByVolume(true);
                                      if (startDate && endDate) {
                                        // Format dates to yyyy-mm-dd for contract price ranking API
                                        const startDateStr = formatDateForChartAPI(startDate);
                                        const endDateStr = formatDateForChartAPI(endDate);
                                        await loadContractPriceRanking(
                                          startDateStr || undefined,
                                          endDateStr || undefined,
                                          true
                                        );
                                      } else {
                                        await loadContractPriceRanking(undefined, undefined, true);
                                      }
                                      hasLoadedInitialData.current = true;
                                    }
                                  }
                                } catch (error) {
                                  console.error('Error fetching current market state:', error);
                                  setNdcValidated(false);
                                } finally {
                                  setIsValidatingNdc(false);
                                }
                              }}
                              disabled={isDisabled}
                            />
                          );
                        })()}
                      </div>
                      {ndcOptions.length === 0 &&
                        supplierName &&
                        formData.conceptName &&
                        formData.packSize && (
                          <span
                            style={{
                              color: '#dc2626',
                              fontSize: '0.875rem',
                              marginTop: '0.25rem',
                              display: 'block',
                            }}
                          >
                            No available NDC found
                          </span>
                        )}
                    </div>
                  </div>

                  {/* GSMS NDC Input Field - Only shown for NC templates (this section is NC-only due to year tabs) */}
                  <div
                    className={classes.formField}
                    style={{ maxWidth: '500px', marginBottom: '1.5rem' }}
                  >
                    <label>GSMS NDC</label>
                    <input
                      type="text"
                      className={`p-inputtext p-component ${classes.gsmsNdcInput}`}
                      value={formData.gsms_ndc || ''}
                      onChange={e => {
                        const formatted = formatNDC(e.target.value);
                        handleFieldChange('gsms_ndc', formatted);

                        // For NC template, update GSMS NDC for ALL years (0-4)
                        // GSMS NDC is the same for all years
                        if (activePackSizeTab !== null) {
                          setYearFormData(prev => {
                            const updated = { ...prev };
                            if (!updated[activePackSizeTab]) {
                              updated[activePackSizeTab] = {};
                            }
                            // Update all years (0-4) with the same GSMS NDC value
                            for (let yearIndex = 0; yearIndex <= 4; yearIndex++) {
                              updated[activePackSizeTab][yearIndex] = {
                                ...updated[activePackSizeTab][yearIndex],
                                gsms_ndc: formatted,
                              };
                            }
                            return updated;
                          });
                        }
                      }}
                      placeholder="12345-234-23"
                      maxLength={12} // 5 + 1 + 3 + 1 + 2 = 12 characters with hyphens
                    />
                  </div>

                  {/* Target Launch Date - Readonly field - Base and option years */}
                  <div
                    className={classes.formField}
                    style={{ maxWidth: '500px', marginBottom: '1.5rem' }}
                  >
                    <label>Target Launch Date</label>
                    <InputText
                      value={(() => {
                        // Base date: from base year (index 0) when available, else global formData
                        const baseTargetLaunchDate =
                          activePackSizeTab !== null &&
                          yearFormData[activePackSizeTab]?.[0]?.targetLaunchDate != null
                            ? yearFormData[activePackSizeTab][0].targetLaunchDate
                            : formData.targetLaunchDate;
                        const yearOffset =
                          activeYearTab !== null && activeYearTab >= 0 && activeYearTab <= 4
                            ? activeYearTab
                            : 0;
                        return formatTargetLaunchDateForDisplay(baseTargetLaunchDate, yearOffset);
                      })()}
                      disabled
                      readOnly
                    />
                  </div>
                </>
              )}

            {/* Year-specific form sections - similar to Indirect */}
            <div className={classes.packSizeContent}>
              {/* Get current year data */}
              {(() => {
                // For 5-Year Summary (tab index 5), show totals instead of form
                if (activeYearTab === 5) {
                  const summary = calculate5YearSummary();
                  return (
                    <div className={classes.subSection}>
                      <SubSectionHeader
                        title="5-Year Summary"
                        isExpanded={true}
                        onToggle={() => {}}
                      />
                      <div className={classes.subSectionContent}>
                        {/* All Readonly Fields - Equal widths */}
                        <div className={classes.formGrid4}>
                          <FormField label="Supplier Bulk NDC">
                            <InputText
                              value={
                                formData.selectedMfgNdc ||
                                formData.mfg_ndc ||
                                formData.gsms_mfg_ndc ||
                                summary.yearData[0]?.supplierBulkNdc ||
                                ''
                              }
                              disabled
                              readOnly
                            />
                          </FormField>
                          <FormField label="GSMS NDC">
                            <InputText
                              value={formData.gsms_ndc || summary.yearData[0]?.gsmsNdc || ''}
                              disabled
                              readOnly
                            />
                          </FormField>
                          <FormField label="Revenue (5-Year Total)">
                            <InputText
                              value={
                                isNaN(summary.totalRevenue)
                                  ? '$0.00'
                                  : `$${summary.totalRevenue.toFixed(2)}`
                              }
                              disabled
                              readOnly
                            />
                          </FormField>
                          <FormField label="GP ($) (5-Year Total)">
                            <InputText
                              value={
                                isNaN(summary.totalGP) ? '$0.00' : `$${summary.totalGP.toFixed(2)}`
                              }
                              disabled
                              readOnly
                            />
                          </FormField>
                        </div>
                        <div style={{ marginTop: '2rem' }}>
                          <h4
                            style={{
                              marginBottom: '1rem',
                              fontSize: '1rem',
                              fontWeight: 600,
                            }}
                          >
                            Per Year Breakdown
                          </h4>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                <th
                                  style={{
                                    padding: '0.75rem',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                  }}
                                >
                                  Year
                                </th>
                                <th
                                  style={{
                                    padding: '0.75rem',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                  }}
                                >
                                  Revenue
                                </th>
                                <th
                                  style={{
                                    padding: '0.75rem',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                  }}
                                >
                                  GP ($)
                                </th>
                                <th
                                  style={{
                                    padding: '0.75rem',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                  }}
                                >
                                  Gross Profit (%)
                                </th>
                                <th
                                  style={{
                                    padding: '0.75rem',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                  }}
                                >
                                  GP/Unit
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {summary.yearData.map((year, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '0.75rem' }}>{year.year}</td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                    {isNaN(year.revenue) ? '$0.00' : `$${year.revenue.toFixed(2)}`}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                    {isNaN(year.gp) ? '$0.00' : `$${year.gp.toFixed(2)}`}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                    {isNaN(year.grossProfit)
                                      ? '0.00'
                                      : `${year.grossProfit.toFixed(2)}`}
                                    %
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                    {isNaN(year.gpPerUnit)
                                      ? '$0.00'
                                      : `$${year.gpPerUnit.toFixed(2)}`}
                                  </td>
                                </tr>
                              ))}
                              <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 600 }}>
                                <td style={{ padding: '0.75rem' }}>5-Year Total</td>
                                <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                  {isNaN(summary.totalRevenue)
                                    ? '$0.00'
                                    : `$${summary.totalRevenue.toFixed(2)}`}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                  {isNaN(summary.totalGP)
                                    ? '$0.00'
                                    : `$${summary.totalGP.toFixed(2)}`}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                  {isNaN(summary.totalGrossProfit)
                                    ? '0.00'
                                    : `${summary.totalGrossProfit.toFixed(2)}`}
                                  %
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                  {isNaN(summary.totalGpPerUnit) || summary.totalGpPerUnit <= 0
                                    ? '-'
                                    : `$${summary.totalGpPerUnit.toFixed(2)}`}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* WAC Analysis: Annual Benefit to Supplier */}
                        <div style={{ marginTop: '2rem' }}>
                          <h4
                            style={{
                              marginBottom: '1rem',
                              fontSize: '1rem',
                              fontWeight: 600,
                            }}
                          >
                            WAC Analysis: Annual Benefit to Supplier
                          </h4>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                <th
                                  style={{
                                    padding: '0.75rem',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                  }}
                                >
                                  Year
                                </th>
                                <th
                                  style={{
                                    padding: '0.75rem',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                  }}
                                >
                                  Annual Benefit
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {summary.yearData.map((year, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '0.75rem' }}>{year.year}</td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                    {isNaN(year.annualBenefit)
                                      ? '$0.00'
                                      : `$${year.annualBenefit.toFixed(2)}`}
                                  </td>
                                </tr>
                              ))}
                              <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 600 }}>
                                <td style={{ padding: '0.75rem' }}>5-Year Total</td>
                                <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                  {isNaN(summary.totalAnnualBenefit)
                                    ? '$0.00'
                                    : `$${summary.totalAnnualBenefit.toFixed(2)}`}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Access nested structure: yearFormData[activePackSizeTab][activeYearTab]
                // CRITICAL: Use yearFormDataRef for immediate access to latest blendedWhlsFee
                // This ensures the UI shows the recalculated value even before state updates
                const yearDataFromRef =
                  activePackSizeTab !== null
                    ? yearFormDataRef.current[activePackSizeTab]?.[activeYearTab as number]
                    : undefined;
                const yearDataFromState =
                  activePackSizeTab !== null
                    ? yearFormData[activePackSizeTab]?.[activeYearTab as number]
                    : undefined;

                // CRITICAL: Prioritize ref (most up-to-date), then state, then formData
                // But also check if formData has the correct discounted value and use it if ref/state don't
                let currentYearData =
                  activePackSizeTab !== null
                    ? yearDataFromRef || yearDataFromState || formData || {}
                    : formData || {};
                // Populate GSMS Pack Size from active pack size tab so it displays correctly
                const activePackTab = packSizeTabs?.find(tab => tab.index === activePackSizeTab);
                if (activePackTab?.packSize) {
                  currentYearData = {
                    ...currentYearData,
                    packSize: activePackTab.packSize,
                    gsmsPackSize: activePackTab.packSize,
                  };
                }
                // CRITICAL: If ref/state don't have the discounted value but formData does, use formData
                // This handles the case where ref hasn't been updated yet on first tab switch
                if (activePackSizeTab !== null && activeYearTab !== null && activeYearTab !== 5) {
                  const refValue = yearDataFromRef?.gsmsNetCostFromMfg;
                  const _stateValue = yearDataFromState?.gsmsNetCostFromMfg;
                  const formDataValue = formData.gsmsNetCostFromMfg;

                  // If formData has a discounted value (different from non-discounted) and ref/state don't, use formData
                  if (formDataValue && formData.supplierDiscount && formData.supplierDiscount > 0) {
                    const gsmsCostFromMfg = parseFloat(formData.gsmsCostFromMfg) || 0;
                    if (gsmsCostFromMfg > 0) {
                      // Check if formData value is discounted (should be less than gsmsCostFromMfg)
                      const formDataNum = parseFloat(formDataValue);
                      if (
                        formDataNum < gsmsCostFromMfg &&
                        (!refValue || parseFloat(refValue) >= gsmsCostFromMfg)
                      ) {
                        // formData has discounted value, use it
                        currentYearData = {
                          ...currentYearData,
                          gsmsNetCostFromMfg: formDataValue,
                          supplierDiscount: formData.supplierDiscount,
                          supplierCode: formData.supplierCode,
                        };
                      }
                    }
                  }
                }

                // CRITICAL: Always derive contractAnnualRevenue/GP from primary fields at
                // display time so Gross Profit and GP/Unit are correct on every tab switch
                currentYearData = deriveContractRevenueAndGPForDisplay(currentYearData);

                return (
                  <>
                    <div ref={el => (sectionRefs.current['current-market-state'] = el)}>
                      <CurrentMarketStateSection
                        isExpanded={expandedSections.currentMarket}
                        onToggle={() => toggleSection('currentMarket')}
                        formData={currentYearData}
                        templateData={templateData}
                        validationErrors={validationErrors}
                        onFieldChange={handleFieldChange}
                        onFieldBlur={handleFieldBlur}
                        onFieldKeyDown={handleFieldKeyDown}
                        ndcValidated={currentYearData.ndcValidated || false}
                        ndcOptions={ndcOptions}
                        templateType={templateType}
                        vaDataAsOf={bidDetails?.va_data_as_of ?? null}
                        dodDataAsOf={bidDetails?.dod_data_as_of ?? null}
                      />
                    </div>

                    <div ref={el => (sectionRefs.current['cost-analysis'] = el)}>
                      <CogsBuildupSection
                        isExpanded={expandedSections.cogsBuildUp}
                        onToggle={() => toggleSection('cogsBuildUp')}
                        formData={currentYearData}
                        isDirect={true}
                        isIndirect={false}
                        onFieldChange={handleFieldChange}
                        onFieldBlur={handleFieldBlur}
                        onFieldKeyDown={handleFieldKeyDown}
                        gsmsHndlgType={gsmsHndlgType}
                        packSizeTabs={packSizeTabs}
                        activePackSizeTab={activePackSizeTab ?? undefined}
                      />
                    </div>

                    <div ref={el => (sectionRefs.current['pricing-strategy'] = el)}>
                      <PricingCalculatorSection
                        isExpanded={expandedSections.pricingCalc}
                        onToggle={() => toggleSection('pricingCalc')}
                        formData={currentYearData}
                        validationErrors={validationErrors}
                        analysisType={analysisType}
                        productType={undefined}
                        templateType={templateType}
                        onFieldChange={handleFieldChange}
                        onFieldBlur={handleFieldBlur}
                        onFieldKeyDown={handleFieldKeyDown}
                      />
                    </div>

                    <PackSizeOutputSection
                      isExpanded={expandedSections.packSize}
                      onToggle={() => toggleSection('packSize')}
                      formData={currentYearData}
                      templateType={templateType}
                      productType={productType as 'direct' | 'indirect' | null | undefined}
                    />

                    <div ref={el => (sectionRefs.current['competitive-analysis'] = el)}>
                      <CompetitiveAnalysisSection
                        isExpanded={expandedSections.competitive}
                        onToggle={() => toggleSection('competitive')}
                        bidId={bidId ? parseInt(bidId) : 0}
                        concept={currentYearData.conceptName || ''}
                        packSize={parseFloat(currentYearData.packSize) || 0}
                        chartDateRange={chartDateRange}
                        tempDateRange={tempDateRange}
                        calendarViewDates={calendarViewDates}
                        selectedCompany={selectedCompany}
                        isLoadingMarketTrends={isLoadingMarketTrends}
                        isLoadingMarketShare={isLoadingMarketShare}
                        volumeByCot={volumeByCot}
                        isLoadingVolumeByCot={isLoadingVolumeByCot}
                        chartOptions={marketTrendsOptions}
                        onDateRangeChange={handleDateRangeChange}
                        onApplyFilter={handleApplyDateFilter}
                        onCalendarViewDateChange={setCalendarViewDates}
                        onMarketShareClick={handleMarketShareClick}
                        onChartModalOpen={() => setShowChartModal(true)}
                      />
                    </div>

                    <div ref={el => (sectionRefs.current['contract-price-ranking'] = el)}>
                      <ContractPriceRankingSection
                        contractPriceRankingData={contractPriceRanking}
                        isExpanded={expandedSections.contractPrice}
                        onToggle={() => toggleSection('contractPrice')}
                        bidId={bidId ? parseInt(bidId) : 0}
                        concept={currentYearData.conceptName || ''}
                        packSize={parseFloat(currentYearData.packSize) || 0}
                        startDate={currentYearData.usagePeriodStartDate}
                        endDate={currentYearData.usagePeriodEndDate}
                        expandedRows={expandedRows}
                        onRowToggle={handleExpandedRowsChange}
                        isLoading={isLoadingContractPriceRanking}
                        onFieldChange={handleFieldChange}
                        onFieldBlur={handleFieldBlur}
                        onNotesChange={loadNotes}
                        notes={notes}
                        currentUser={getCurrentUser()}
                        bidOwner={bidOwner}
                      />
                    </div>

                    <CommercialPriceRankingSection
                      isExpanded={expandedSections.commercialPrice}
                      onToggle={() => toggleSection('commercialPrice')}
                      commercialPricing={templateData?.commercialPricing || []}
                      templateType={templateType}
                      formData={currentYearData}
                      onFieldChange={handleFieldChange}
                      onFieldBlur={handleFieldBlur}
                      onFieldKeyDown={handleFieldKeyDown}
                    />

                    <div ref={el => (sectionRefs.current['global-output'] = el)}>
                      <NCGlobalOutputSection
                        isExpanded={expandedSections.globalOutput}
                        onToggle={() => toggleSection('globalOutput')}
                        formData={formData}
                        yearFormData={yearFormData}
                        packSizeFormData={packSizeFormData}
                        activePackSizeTab={activePackSizeTab}
                        activeYearTab={activeYearTab}
                        allPackSizeTabs={allPackSizeTabs}
                        bidApprovedBy={bidDetails?.approved_by ?? null}
                        gsmsHndlgType={gsmsHndlgType}
                        onFieldChange={handleFieldChange}
                        onFieldBlur={handleFieldBlur}
                        onFieldKeyDown={handleFieldKeyDown}
                        onSetActivePackSizeTab={(tabIndex: number) => {
                          handlePackSizeTabChange(tabIndex);
                          const tab = allPackSizeTabs.find((t) => t.index === tabIndex);
                          if (tab?.conceptName) {
                            setSelectedConcept(tab.conceptName);
                          }
                        }}
                        onSetActiveYearTab={handleYearTabChange}
                        copyForAllYears={copyForAllYears}
                      />
                    </div>

                    <div ref={el => (sectionRefs.current['wac-analysis'] = el)}>
                      <WACAnalysisSection
                        isExpanded={expandedSections.wacAnalysis}
                        onToggle={() => toggleSection('wacAnalysis')}
                        formData={currentYearData}
                        packSizeFormData={(() => {
                          // For NC template, aggregate data from ALL pack sizes (all concepts) for the current year
                          if (
                            templateType === 'nc' &&
                            activeYearTab !== null &&
                            activeYearTab !== 5
                          ) {
                            const aggregatedData: Record<number, any> = {};
                            // Use allPackSizeTabs to get ALL concepts (325mg, 500mg, etc.), not just selected concept
                            allPackSizeTabs.forEach(tab => {
                              const tabIndex = tab.index;
                              const yearData =
                                yearFormDataRef.current[tabIndex]?.[activeYearTab] ||
                                yearFormData[tabIndex]?.[activeYearTab] ||
                                {};
                              const baseData = packSizeFormData[tabIndex] || {};
                              // Merge base data with year-specific output, then derive revenue/GP at display time
                              aggregatedData[tabIndex] = deriveContractRevenueAndGPForDisplay({
                                ...baseData,
                                ...yearData,
                              });
                            });
                            return aggregatedData;
                          }
                          return packSizeFormData;
                        })()}
                        templateType={templateType}
                      />
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Market Landscape Section - Only for NC template, placed below Product Details */}
      <div ref={el => (sectionRefs.current['market-landscape'] = el)}>
        <MarketLandscapeSection
          isExpanded={expandedSections.marketLandscape}
          onToggle={() => toggleSection('marketLandscape')}
          bidId={parseInt(bidId || '0')}
          productName={bidDetails?.product_name || ''}
          onDataChange={setMarketLandscapeData}
          externalData={marketLandscapeData}
        />
      </div>
    </>
  );
}
